import React from 'react';
import { Pressable, Text, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../styles/theme';

interface Props {
  /** Items on the shelves. The row names it, and hides itself at zero. */
  count: number;
  busy?: boolean;
  onPress: () => void;
}

/**
 * "Clear pantry · 767 items", at the foot of the pantry.
 *
 * Destructive per DESIGN.md: transparent, `destructive` text, no border and no
 * fill. Labelled rather than an icon, and carrying the count, because it wipes
 * every shelf with no undo — an unlabelled trash icon next to the receipt
 * scanner is the one place this must not be.
 */
export function ClearPantryRow({ count, busy = false, onPress }: Props) {
  const { t } = useTranslation();

  if (count === 0) return null;

  return (
    <Pressable
      onPress={onPress}
      disabled={busy}
      accessibilityRole="button"
      accessibilityState={{ disabled: busy }}
      testID="pantry-clear-row"
      style={({ pressed }) => [styles.row, (pressed || busy) && styles.rowPressed]}
    >
      <Text style={styles.label}>
        {busy ? t('pantry.clear.busy') : t('pantry.clear.row', { count })}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    alignSelf: 'flex-start',
    paddingVertical: 12,
    paddingHorizontal: 4,
    marginBottom: 8,
  },
  rowPressed: {
    opacity: 0.6,
  },
  label: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    lineHeight: 16,
    color: Paprika.destructive,
  },
});
